import './index.css'
import 'draft-js-linkify-plugin/lib/plugin.css'
import cn from 'clsx'
import {
  ContentState,
  convertFromHTML,
  convertFromRaw,
  convertToRaw,
  EditorState,
  Modifier,
  RichUtils,
  SelectionState,
} from 'draft-js'
import createBlockDndPlugin from 'draft-js-drag-n-drop-plugin'
import createFocusPlugin from 'draft-js-focus-plugin'
import createImagePlugin from 'draft-js-image-plugin'
import createLinkifyPlugin from 'draft-js-linkify-plugin'
import Editor from 'draft-js-plugins-editor'
// plugins
import { composeDecorators } from 'draft-js-plugins-editor'
import { getSelectionEntity } from 'draftjs-utils'
import * as React from 'react'
import { ENTITY_TYPE } from '@disco/constants/content-editor'
import { getEntityTypeByEntityKey } from '@disco/content-editor/editor-utils'
import exportToHtml from '@disco/content-editor/html-export'
import exportToMjml from '@disco/content-editor/mjml-export'
import ImageComponent from './custom-blocks/image'
import createButtonPlugin from './plugins/button'
import createCustomLinkPlugin from './plugins/custom-link'
import createMergeTagPlugin from './plugins/merge-tag'
import RichTextToolbar from './toolbar'
import { FONT_SIZES } from './toolbar/constants'
import blockStyleFn from './utils/block-style'
import linkifyState from './utils/linkify-state'
const blockDndPlugin = createBlockDndPlugin()
// @ts-ignore
const focusPlugin = createFocusPlugin({
  theme: {
    focused: 'editor-element--focused',
  },
})
const decorator = composeDecorators(
  blockDndPlugin.decorator,
  focusPlugin.decorator,
)
const imagePlugin = createImagePlugin({
  decorator,
  imageComponent: ImageComponent,
  theme: {
    image: 'editor-image',
  },
})
const buttonPlugin = createButtonPlugin({
  decorator: focusPlugin.decorator,
  theme: {
    button: 'rte-button',
  },
})
const mergeTagPlugin = createMergeTagPlugin()
const defaultPlugins = [createLinkifyPlugin(), createCustomLinkPlugin()]
const campaignEditorPlugins = [
  ...defaultPlugins,
  imagePlugin,
  buttonPlugin,
  mergeTagPlugin,
  blockDndPlugin,
  focusPlugin,
]
type HtmlContent = string
type MjmlContent = string
type PlainText = string
type Props = {
  readOnly?: boolean
  invalid?: boolean
  onChange: (
    rawContent: Record<string, any> | null | undefined,
    html?: HtmlContent | null | undefined,
    mjml?: MjmlContent | null | undefined,
    plainText?: PlainText | null | undefined,
  ) => any
  initialContent?: Record<string, any> | null | undefined
  className?: string
  showToolbar?: boolean
  type: 'default' | 'email'
  editorRef?: React.LegacyRef<Editor> | any
  maxImageWidth?: number
}

/**
 * Generates font size styles for the editor
 * @returns Object mapping font size class names to CSS styles
 */
const getFontStyles = () => {
  let result = {}
  FONT_SIZES.forEach(size => {
    result[`fontsize-${size}`] = {
      fontSize: size,
    }
  })
  return result
}

const colorKey = 'color-'
const backgroundColorKey = 'bgcolor-'

const getKeyValue = (key, style) => (style ? style.replace(key, '') : undefined)

const getColorStyles = (
  contentState: Record<string, any> | null | undefined,
) => {
  let result = {}
  const blocks = contentState?.blocks ?? []
  blocks.forEach(block => {
    if (block.inlineStyleRanges)
      block.inlineStyleRanges.forEach(({ style }) => {
        if (style.indexOf(colorKey) === 0) {
          result[style] = {
            color: getKeyValue(colorKey, style),
          }
        } else if (style.indexOf(backgroundColorKey) === 0) {
          result[style] = {
            backgroundColor: getKeyValue(backgroundColorKey, style),
          }
        }
      })
  })
  return result
}

/**
 * RichTextEditor component for advanced text editing
 *
 * A comprehensive rich text editor built on Draft.js with support for text formatting,
 * images, buttons, merge tags, and custom styling. Provides both default and email-specific
 * modes with different plugin configurations. Exports content in multiple formats including
 * HTML, MJML, and plain text.
 *
 * @param props - Component props
 * @param props.readOnly - Whether the editor is in read-only mode
 * @param props.invalid - Whether the editor content is invalid
 * @param props.onChange - Callback called when content changes, receives raw content, HTML, MJML, and plain text
 * @param props.initialContent - Initial content to load into the editor
 * @param props.className - Additional CSS class names
 * @param props.showToolbar - Whether to show the formatting toolbar
 * @param props.type - Editor type: 'default' for basic editing or 'email' for campaign editing with advanced features
 * @param props.editorRef - Ref to access the underlying editor instance
 * @param props.maxImageWidth - Maximum width for uploaded images
 * @returns JSX element containing the rich text editor
 */
export default class RichTextEditor extends React.Component<
  Props,
  {
    readOnly: boolean
    editorState: EditorState
    customStyleMap: Record<string, any>
  }
> {
  static defaultProps: {
    type: 'default' | 'email'
  } = {
    type: 'default',
  }
  blockChange: boolean = false

  constructor(props: Props) {
    super(props)
    this.state = {
      editorState:
        props.initialContent && typeof props.initialContent === 'object'
          ? EditorState.createWithContent(convertFromRaw(props.initialContent))
          : EditorState.createEmpty(),
      customStyleMap: {
        ...getFontStyles(),
        ...getColorStyles(props.initialContent),
      },
      readOnly: this.props.readOnly || false,
    }
  }

  setEditorContent: (content: any) => void = (content: Record<string, any>) => {
    this.setState({
      editorState: EditorState.createWithContent(convertFromRaw(content)),
    })
  }
  setReadOnlyMode: (readOnly: boolean) => void = (readOnly: boolean) => {
    this.setState({
      readOnly,
    })
  }
  _onStyleMapUpdate: (styleMapUpdate: any) => void = (
    styleMapUpdate: Record<string, any>,
  ) => {
    this.setState(
      {
        customStyleMap: { ...this.state.customStyleMap, ...styleMapUpdate },
      },
      () => console.log(this.state.customStyleMap),
    )
  }
  _onChange: (editorState: any) => any | void = (
    editorState: Record<string, any>,
  ) => {
    if (this.blockChange) {
      this.blockChange = false
      return
    }

    this.setState({
      editorState,
    })
    const content = linkifyState(editorState).getCurrentContent()
    const { onChange } = this.props

    if (onChange) {
      const rawContent = convertToRaw(content)
      const plainText = content.getPlainText()
      const contentExists = rawContent.blocks
        ? rawContent.blocks.some(b => b.text !== '')
        : false
      if (!contentExists) return onChange(null, null, null, null)
      // too many exports in frequently called function
      onChange(
        rawContent,
        exportToHtml(content),
        exportToMjml(content, {}),
        plainText,
      )
    }
  }
  _onTab: (e: React.SyntheticEvent) => void = (e: React.SyntheticEvent) => {
    e.preventDefault()
    const maxDepth = 4

    this._onChange(RichUtils.onTab(e, this.state.editorState, maxDepth))
  }
  _onReturn: (e: React.SyntheticEvent, editorState: EditorState) => void = (
    e,
    editorState,
  ) => {
    const contentState = editorState.getCurrentContent()
    const selection = editorState.getSelection()
    const block = contentState.getBlockForKey(selection.getStartKey())
    // block onChange when user press Enter when focused on atomic block
    // this will cause bugs if not blocked
    if (block.get('type') === 'atomic') this.blockChange = true
  }
  _handleKeyCommand: (
    command: string,
    editorState: EditorState,
  ) => 'handled' | 'not-handled' = (command, editorState) => {
    const contentState = editorState.getCurrentContent()
    const selection = editorState.getSelection()
    const block = contentState.getBlockForKey(selection.getStartKey())
    const entityKey = getSelectionEntity(editorState)
    const entityType = entityKey
      ? getEntityTypeByEntityKey(editorState, entityKey)
      : null
    let newState = null
    const manuallyHandledEntities = [ENTITY_TYPE.IMAGE, ENTITY_TYPE.BUTTON]

    if (
      block.getType() === 'atomic' &&
      entityKey &&
      entityType != null &&
      manuallyHandledEntities.includes(entityType)
    ) {
      // manually adjust selection to entire atomic block so selected block
      // will be deleted corretly
      const key = selection.getFocusKey()
      const keyAfter = contentState.getKeyAfter(key)
      const newSelection = SelectionState.createEmpty(keyAfter)
      const updatedState = EditorState.forceSelection(editorState, newSelection)
      newState = RichUtils.handleKeyCommand(updatedState, command)
    } else {
      newState = RichUtils.handleKeyCommand(editorState, command)
    }

    // read about handled/not-handled
    // https://draftjs.org/docs/advanced-topics-key-bindings.html
    if (newState) {
      this._onChange(newState)

      return 'handled'
    }

    return 'not-handled'
  }
  _handlePastedText: (
    text: string,
    html: string,
    editorState: EditorState,
  ) => 'handled' | 'not-handled' = (text, html, editorState) => {
    if (html) {
      // filter out non-breaking spaces from pasted html as usually they break
      // pasted html
      const filteredHtml = html.split(String.fromCharCode(160)).join(' ')
      const htmlFragment = convertFromHTML(filteredHtml)

      if (htmlFragment) {
        const { contentBlocks, entityMap } = htmlFragment

        if (contentBlocks) {
          const blockMap = ContentState.createFromBlockArray(
            contentBlocks,
            entityMap,
          ).getBlockMap()
          const newState = Modifier.replaceWithFragment(
            editorState.getCurrentContent(),
            editorState.getSelection(),
            blockMap,
          )

          this._onChange(
            EditorState.push(editorState, newState, 'insert-fragment'),
          )

          return 'handled'
        }
      }
    }

    return 'not-handled'
  }

  render(): React.ReactElement<'div'> {
    const { type, editorRef } = this.props
    const readOnly = this.state.readOnly || this.props.readOnly
    const richTextEditor = cn('rich-text-editor', this.props.className, {
      'rich-text-editor--default': type === 'default',
      'rich-text-editor--email': type === 'email',
      'rich-text-editor--readonly': readOnly,
    })
    const { editorState } = this.state
    return (
      <div className={richTextEditor} aria-invalid={this.props.invalid}>
        {this.props.showToolbar && (
          <RichTextToolbar
            editorState={linkifyState(editorState)}
            customStyleMap={this.state.customStyleMap}
            onStyleMapUpdate={this._onStyleMapUpdate}
            onChange={this._onChange}
            maxImageWidth={this.props.maxImageWidth}
          />
        )}
        <Editor
          // @ts-expect-error
          editorState={editorState}
          onChange={this._onChange}
          plugins={type === 'default' ? defaultPlugins : campaignEditorPlugins}
          readOnly={readOnly}
          customStyleMap={this.state.customStyleMap}
          spellCheck
          handleKeyCommand={this._handleKeyCommand}
          handlePastedText={this._handlePastedText}
          blockStyleFn={blockStyleFn}
          handleReturn={this._onReturn}
          onTab={this._onTab}
          // SLAM-1942: React 19 ref callbacks must return void, so wrap the body in braces.
          ref={
            editorRef
              ? r => {
                  editorRef(r)
                }
              : undefined
          }
          wrapperRef={this}
        />
      </div>
    )
  }
}
